/*
Related Products Selector
*/

class RelatedProductsSelector extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    const label = this.querySelector(".active-label");
    const flavors = this.querySelectorAll("[data-flavor]");
    label.innerHTML = this.dataset.selected;

    flavors.forEach((flavor) => {
      flavor.addEventListener("click", () => {
        label.innerHTML = flavor.dataset.flavor;
        this.dataset.selected = flavor.dataset.flavor;
      });

      flavor.addEventListener("mouseover", () => {
        label.innerHTML = flavor.dataset.flavor;
      });

      this.addEventListener("mouseout", () => {
        label.innerHTML = this.dataset.selected;
      });
    });
  }
}

customElements.define("related-products-selector", RelatedProductsSelector);

/*
Product Fixed Bar
*/

class ProductFixedBar extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector("[data-bar]");
    if (!this.bar) return;
    this.controller = this.closest("product-details-controller");
    this.anchor = document.querySelector("#pdp-add-cart") || document.querySelector("[data-pdp-details]");
    this.panel = this.querySelector("[data-bar-panel]");
    this.footer = document.querySelector("#shopify-section-footer");
    this.toggles = Array.from(this.querySelectorAll("[data-bar-toggle]"));
    this._onScroll = () => this.update();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll);
    this.update();

    this.querySelectorAll("[data-bar-option]").forEach((radio) => {
      radio.addEventListener("change", () => this.choose(radio.dataset.barOption, radio.closest("[data-bar-group]")));
    });
    this.querySelectorAll("[data-bar-variant]").forEach((radio) => {
      radio.addEventListener("change", () => this.chooseVariant(radio.value));
    });
    this.querySelectorAll("[data-bar-plan]").forEach((select) => {
      select.addEventListener("change", () => this.choose("subscribe", select.closest("[data-bar-group]")));
    });
    if (this.toggles.length && this.panel) {
      this.toggles.forEach((toggle) => {
        toggle.addEventListener("click", () => this.setOpen(!this.bar.classList.contains("is-open")));
      });
      this._onOutside = (e) => {
        if (!this.contains(e.target)) this.setOpen(false);
      };
      document.addEventListener("click", this._onOutside);
      this._onKey = (e) => {
        if (e.key === "Escape") this.setOpen(false);
      };
      document.addEventListener("keydown", this._onKey);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onScroll);
    if (this._onOutside) document.removeEventListener("click", this._onOutside);
    if (this._onKey) document.removeEventListener("keydown", this._onKey);
  }

  update() {
    const rect = this.anchor ? this.anchor.getBoundingClientRect() : null;
    const pastAnchor = rect ? rect.bottom < 0 || rect.top > window.innerHeight : window.scrollY > 800;
    const atFooter = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
    const show = pastAnchor && !atFooter;
    this.bar.classList.toggle("is-active", show);
    if (!show) this.setOpen(false);
  }

  setOpen(open) {
    if (!this.panel || !this.toggles.length) return;
    this.toggles.forEach((toggle) => toggle.setAttribute("aria-expanded", open ? "true" : "false"));
    this.bar.classList.toggle("is-open", open);
  }

  setOneTime() {
    this.bar.classList.add("is-onetime");
    this.querySelectorAll('[data-bar-option="onetime"]').forEach((radio) => (radio.checked = true));
    this.querySelectorAll(".pdp-purchase__card").forEach((card) => {
      card.classList.toggle("is-selected", !!card.querySelector("input:checked"));
    });
  }

  chooseVariant(variantId) {
    const url = new URL(window.location);
    url.searchParams.set("variant", variantId);
    url.searchParams.delete("selling_plan");
    window.history.replaceState({}, "", url);
    if (this.controller) this.controller.updateProductDetailsHTML();
  }

  choose(type, group) {
    const url = new URL(window.location);
    if (type === "onetime") {
      url.searchParams.delete("selling_plan");
      if (this.controller) this.controller.explicitOneTime = true;
    } else {
      const select = (group && group.querySelector("[data-bar-plan]")) || this.querySelector("[data-bar-plan]");
      const radio = (group && group.querySelector('[data-bar-option="subscribe"]')) || this.querySelector('[data-bar-option="subscribe"]');
      const planId = (select && select.value) || (radio && radio.dataset.planId) || "";
      if (!planId) return;
      url.searchParams.set("selling_plan", planId);
      if (this.controller) this.controller.explicitOneTime = false;
    }
    window.history.replaceState({}, "", url);
    if (this.controller) this.controller.updateProductDetailsHTML();
  }
}

customElements.define("product-fixed-bar", ProductFixedBar);

class ProductDetailsController extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;
    this.subscriptionPurchaseCheked = false;

    this.addVariantChangeEvents();
    this.addSubscribeChangeEvents();
  }

  async updateProductDetailsHTML() {
    const url = new URL(window.location);

    const variantParam = url.searchParams.get("variant") ? `variant=${url.searchParams.get("variant")}` : "";
    const sellingPlanParam = url.searchParams.get("selling_plan")
      ? `&selling_plan=${url.searchParams.get("selling_plan")}`
      : "";

    const focusEl = document.activeElement;

    const rawPathWithParams = `/products/${this.productHandle}?sections=${this.sectionId}&${variantParam}${sellingPlanParam}`;

    try {
      const rawResponse = await fetch(rawPathWithParams);

      if (!rawResponse.ok) {
        window.ghiaReport &&
          window.ghiaReport("pdp_error", {
            error_message: "section render failed",
            error_stage: "fetch",
            http_status: rawResponse.status,
            product_handle: this.productHandle,
          });
        return;
      }

      const response = await rawResponse.json();

      const mainProductSubscriptionEl = response[this.sectionId];
      const element = this.htmlToElement(mainProductSubscriptionEl);
      const replacement = element && element.querySelector("product-details-controller");

      if (!replacement) {
        window.ghiaReport &&
          window.ghiaReport("pdp_error", {
            error_message: "section markup missing controller",
            error_stage: "parse",
            product_handle: this.productHandle,
          });
        return;
      }

      const target = this.querySelector("[data-pdp-details]");
      const source = replacement.querySelector("[data-pdp-details]");
      if (target && source) {
        target.innerHTML = source.innerHTML;
      } else {
        this.innerHTML = replacement.innerHTML;
      }

      this.addSubscribeChangeEvents();
      this.addVariantChangeEvents();

      if (this.explicitOneTime) this.restoreOneTimeSelection();

      if (focusEl && focusEl.id) {
        const restored = document.getElementById(focusEl.id);
        if (restored) restored.focus();
      }
    } catch (e) {
      window.ghiaReport &&
        window.ghiaReport("pdp_error", {
          error_message: e && e.message ? e.message : String(e),
          error_stage: "exception",
          product_handle: this.productHandle,
        });
    }
  }

  addVariantChangeEvents() {
    const variantsSelectorEl = this.querySelectorAll("[name='variant-selector']");

    if (!variantsSelectorEl.length > 0) return;

    variantsSelectorEl.forEach((option) => {
      option.addEventListener("change", () => {
        this.querySelectorAll(".pdp-pill").forEach((pill) => {
          pill.classList.toggle("is-selected", pill.contains(option));
        });

        const url = new URL(window.location);
        const droppedPlan = url.searchParams.get("selling_plan");

        url.searchParams.set("variant", option.value);
        url.searchParams.delete("selling_plan");

        if (droppedPlan) {
          window.ghiaReport &&
            window.ghiaReport("subscription_reset", {
              error_message: "selling plan cleared by variant change",
              product_handle: this.productHandle,
              variant_id: option.value,
              selling_plan: droppedPlan,
            });
        }

        window.history.replaceState({}, "", url);

        this.updateProductDetailsHTML();
      });
    });
  }

  addSubscribeChangeEvents() {
    const purchaseOptions = this.querySelectorAll("[name='purchase-options']");
    const sellingPlanSelectorEl = this.querySelector("#selling-plan-selector");

    if (!purchaseOptions || !sellingPlanSelectorEl) return;

    purchaseOptions.forEach((option) => {
      option.addEventListener("change", () => {
        const url = new URL(window.location);

        if (option.id == "subscribe-purchase") {
          this.explicitOneTime = false;
          this.sellingPlanSelector();
        } else {
          this.subscriptionPurchaseCheked = false;
          this.explicitOneTime = true;
          url.searchParams.delete("selling_plan");

          window.history.replaceState({}, "", url);
          this.updateProductDetailsHTML(this.productHandle);
        }
      });
    });

    sellingPlanSelectorEl.addEventListener("change", () => {
      this.sellingPlanSelector();
    });
  }

  restoreOneTimeSelection() {
    const oneTime = this.querySelector("#one-time-purchase");
    const subscribe = this.querySelector("#subscribe-purchase");

    if (!oneTime || oneTime.checked) return;

    oneTime.checked = true;
    if (subscribe) subscribe.checked = false;
    this.querySelectorAll("[data-subscription-body]").forEach((el) => el.classList.add("hidden"));
    this.querySelectorAll(".pdp-purchase__card").forEach((card) => {
      card.classList.toggle("is-selected", !!card.querySelector("input:checked"));
    });

    this.querySelectorAll('[name="selling_plan"]').forEach((input) => {
      input.value = "";
    });
    this.querySelectorAll("product-fixed-bar").forEach((bar) => bar.setOneTime && bar.setOneTime());

  }

  sellingPlanSelector() {
    const url = new URL(window.location);
    const sellingPlanSelectorEl = this.querySelector("#selling-plan-selector");

    if (sellingPlanSelectorEl) {
      url.searchParams.set("selling_plan", sellingPlanSelectorEl.value);
      window.history.replaceState({}, "", url);
      this.updateProductDetailsHTML(this.productHandle);
    }
  }

  htmlToElement(html) {
    var template = document.createElement("template");
    html = html.trim();
    template.innerHTML = html;
    return template.content.firstChild;
  }
}

customElements.define("product-details-controller", ProductDetailsController);

class PdpMedia extends HTMLElement {
  connectedCallback() {
    this.wrap = this.closest(".pdp-media-wrap") || this.parentElement;
    this.dots = Array.from(this.wrap.querySelectorAll("[data-gallery-dot]"));
    this.slides = Array.from(this.querySelectorAll("[data-media-index]"));
    if (!this.dots.length || !this.slides.length) return;

    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => this.scrollToIndex(Number(dot.dataset.galleryDot)));
    });

    let raf = 0;
    this.addEventListener("scroll", () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.syncDots());
    }, { passive: true });
    this._onResize = () => this.syncDots();
    window.addEventListener("resize", this._onResize);
    this.syncDots();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
  }

  positions() {
    const slideWidth = this.slides[0] ? this.slides[0].getBoundingClientRect().width : 0;
    const perView = slideWidth ? Math.max(1, Math.round(this.clientWidth / slideWidth)) : 1;
    return Math.max(1, this.slides.length - perView + 1);
  }

  scrollToIndex(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const origin = this.slides[0] ? this.slides[0].offsetLeft : 0;
    this.scrollTo({ left: slide.offsetLeft - origin, behavior: "smooth" });
  }

  syncDots() {
    const origin = this.slides[0] ? this.slides[0].offsetLeft : 0;
    let index = 0;
    let best = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - origin - this.scrollLeft);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });
    const positions = this.positions();
    index = Math.min(index, positions - 1);
    this.dots.forEach((dot, i) => {
      dot.hidden = i >= positions;
      if (i === index) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
    });
  }
}

customElements.define("pdp-media", PdpMedia);

const hueReviewCache = new Map();

class PdpReviews extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector("[data-review-modal]");
    this.grid = this.querySelector("[data-review-grid]");
    this.template = this.querySelector("[data-review-card-template]");
    this.limit = Number(this.dataset.limit) || 4;
    this.product = {
      productTitle: this.dataset.productTitle,
      productPrice: this.dataset.productPrice,
      productImage: this.dataset.productImage,
      productUrl: this.dataset.productUrl,
      variantId: this.dataset.variantId,
      available: this.dataset.available,
    };
    this.reviews = [];

    const cards = Array.from(this.querySelectorAll("[data-video-card][data-video-src]"));
    if (cards.length) {
      cards.forEach((card) => {
        const index = Number(card.dataset.reviewIndex);
        this.reviews[index] = { ...card.dataset, ...this.product };
        card.addEventListener("click", () => this.open(index));
      });
      this.setColumns(cards.length);
      return;
    }

    if (this.dataset.hueProductId) this.loadHue(this.dataset.hueProductId);
    else this.hidden = true;
  }

  async loadHue(productId) {
    try {
      let reviews = hueReviewCache.get(productId);
      if (!reviews) {
        const response = await fetch(
          `https://api.poweredbyhue.app/shoppers-api/v1/products/${productId}?data[]=reviews`
        );
        if (!response.ok) throw new Error(`hue ${response.status}`);
        const payload = await response.json();
        const data = (payload.reviews && payload.reviews.data) || [];
        reviews = [];
        data.forEach((review) => {
          if (review.isVisible === false) return;
          const assets = (review.creator && review.creator.assets) || [];
          const video = assets.find((asset) => asset.type && asset.type.type === "VIDEO" && asset.url);
          if (!video) return;
          const profile = (review.creator && review.creator.profile) || {};
          const first = (profile.firstName || "").trim();
          const last = (profile.lastName || "").trim();
          const author = [first, last ? `${last.charAt(0)}.` : ""].filter(Boolean).join(" ") || review.creator.name || "";
          reviews.push({
            videoSrc: video.url,
            poster: video.thumbnailUrl || "",
            author,
            handle: profile.instagramHandle || profile.tiktokHandle || "",
            stars: review.rating || 5,
            title: review.title || "",
            text: review.text || "",
          });
        });
        hueReviewCache.set(productId, reviews);
      }
      this.renderCards(reviews);
    } catch (e) {
      this.hidden = true;
    }
  }

  renderCards(reviews) {
    const items = reviews.slice(0, this.limit);
    if (!items.length || !this.template) {
      this.hidden = true;
      return;
    }
    this.reviews = items.map((review) => ({ ...review, ...this.product }));
    this.grid.innerHTML = "";
    this.reviews.forEach((review, index) => {
      const card = this.template.content.firstElementChild.cloneNode(true);
      const img = card.querySelector("img");
      img.src = review.poster;
      card.setAttribute("aria-label", review.author ? `Play review from ${review.author}` : "Play review");
      card.addEventListener("click", () => this.open(index));
      this.grid.appendChild(card);
    });
    this.setColumns(this.reviews.length);
    this.hidden = false;
  }

  setColumns(count) {
    this.grid.style.gridTemplateColumns = `repeat(${Math.max(2, Math.min(4, count))}, minmax(0, 1fr))`;
  }

  open(index) {
    if (!this.modal || !this.modal.show || !this.reviews.length) return;
    this.modal.show(this.reviews, index, null);
  }
}

customElements.define("pdp-reviews", PdpReviews);

class PdpParty extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-party-track]");
    this.items = Array.from(this.querySelectorAll("[data-party-item]"));
    this.dots = Array.from(this.querySelectorAll("[data-party-dot]"));
    if (!this.track || this.items.length < 2) return;

    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const item = this.items[Number(dot.dataset.partyDot)];
        if (item) this.track.scrollTo({ left: item.offsetLeft, behavior: "smooth" });
      });
    });

    let raf = 0;
    this.track.addEventListener("scroll", () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.sync());
    }, { passive: true });
  }

  sync() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(this.track.scrollLeft / width);
    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
    });
  }
}

customElements.define("pdp-party", PdpParty);
